/**
 * ScopeCoverageModal — the customer-facing "Choose what we watch" picker
 * (Baseline Compliance → Monitoring, scope step): a live capacity gauge, the
 * coverage workloads as checkbox rows (Always on / Recommended / Opt-in), and
 * a collapsible "How these numbers work" explainer.
 *
 * PRESENTATIONAL ONLY — all selection state lives in the parent (SetupJourney
 * owns `selectedProfiles`); this component renders the passed view models and
 * reports intent through callbacks. That keeps it deterministic under the SSR
 * test harness (renderToStaticMarkup, no effects): a plain `role="dialog"`
 * shell (scrim, layer-02 panel) around the Carbon modal frame
 * (components/carbon/ModalFrame: pinned header, scrolling body, the footer
 * button set), rendered inline rather than through a portal.
 *
 * COLOR: tokens only. The gauge is the strong neutral; only an over-capacity
 * selection, which blocks the submit, takes Coral (the Neutral Bad State Rule).
 *
 * COPY: all customer strings come from lib/monitoring-actions (SCOPE_MODAL_COPY,
 * composeScopeExplainer) so the FORBIDDEN-jargon sweep covers them. No internal
 * pipeline names, no capacity nouns, no fabricated cadence figure.
 */

import { forwardRef, useEffect, useRef, useState, type HTMLAttributes } from 'react'
import clsx from 'clsx'

import {
  composeScopeExplainer,
  SCOPE_MODAL_COPY,
  type CapacityMeter,
  type ScopeChip,
} from '@/lib/monitoring-actions'
import { Checkbox } from '@/components/carbon/Checkbox'
import { FIELD_ON_LAYER_02 } from '@/components/carbon/fieldStyles'
import { IconChevronRight } from '@/components/carbon/icons'
import { ModalFrame } from '@/components/carbon/Modal'
import { ProgressBar } from '@/components/carbon/ProgressBar'
import { Tag } from '@/components/carbon/Tag'

export interface ScopeCoverageModalProps {
  /** Coverage workloads with their per-workload counts (composeScopeChips). */
  chips: ScopeChip[]
  /** Live capacity verdict for the current selection (composeCapacityMeter). */
  meter: CapacityMeter
  /** The currently-selected profile ids (owned by the parent). */
  selectedProfiles: string[]
  /** Plain-language read-back (composeSelectionSummary); null → empty state. */
  selectionSummary: string | null
  /** Capacity figure for the explainer copy (coverage.instanceCeiling). */
  instanceCeiling: number
  /** True while the scope→activate chain is in flight — locks the dialog. */
  submitting: boolean
  onToggle: (id: string, kind: string) => void
  onConfirm: () => void
  onClose: () => void
}

/** Which tag (if any) a workload carries. The word carries the meaning; the tag is neutral. */
function tagFor(chip: ScopeChip): string | null {
  if (chip.kind === 'base') return 'Always on'
  if (chip.kind === 'optin') return 'Opt-in'
  // A preselected add-on is the recommended widening (threat protection today).
  if (chip.kind === 'addon' && chip.preselected) return 'Recommended'
  return null
}

/** The modal's title element, carrying the id the dialog is labelled by. */
const ScopeTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(function ScopeTitle(props, ref) {
  return <h2 ref={ref} id="scope-modal-title" {...props} />
})

export default function ScopeCoverageModal({
  chips,
  meter,
  selectedProfiles,
  selectionSummary,
  instanceCeiling,
  submitting,
  onToggle,
  onConfirm,
  onClose,
}: ScopeCoverageModalProps) {
  const [explainerOpen, setExplainerOpen] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Escape-to-close + initial focus (runtime only — effects don't run under the
  // SSR test harness, so the render-shape test is unaffected).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    document.addEventListener('keydown', onKey)
    dialogRef.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, submitting])

  const explainer = composeScopeExplainer(instanceCeiling)
  const sized = meter.state !== 'unsized'
  const noneSelected = selectedProfiles.length === 0
  const close = () => {
    if (!submitting) onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-modal-title"
      className="fixed inset-0 z-[60] flex items-stretch justify-center overflow-y-auto bg-black/60 c-md:items-center c-md:p-4"
      onClick={e => {
        if (e.target === e.currentTarget) close()
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        data-menu=""
        className={clsx(
          'flex w-full flex-col bg-layer-2 text-text-primary outline-none max-c-md:min-h-full c-md:max-h-[min(90vh,48rem)] c-md:max-w-[640px]',
          FIELD_ON_LAYER_02,
        )}
      >
        <ModalFrame
          title={`${SCOPE_MODAL_COPY.headline} ${SCOPE_MODAL_COPY.headlineEm}`}
          subtitle={SCOPE_MODAL_COPY.deck}
          titleAs={ScopeTitle}
          onClose={close}
          secondaryAction={{ label: SCOPE_MODAL_COPY.cancel, onClick: close, disabled: submitting }}
          primaryAction={{
            label: submitting ? 'Setting up your coverage…' : SCOPE_MODAL_COPY.confirm,
            onClick: onConfirm,
            disabled: meter.blocked || noneSelected,
            loading: submitting,
          }}
        >
          {/* ── Capacity gauge ──────────────────────────────────────── */}
          {sized ? (
            <ProgressBar
              value={meter.selectedCount ?? 0}
              max={meter.ceiling}
              status={meter.state === 'over' ? 'error' : 'active'}
              label={
                <span className="tabular-nums">
                  <b className="font-semibold">{meter.selectedCount}</b> of about {meter.ceiling} settings
                </span>
              }
              helper={meter.detail}
            />
          ) : (
            <p className="m-0 text-body text-text-primary">{meter.headline}</p>
          )}

          {/* ── Coverage workloads ──────────────────────────────────── */}
          <ul className="m-0 mt-6 list-none border-t border-line-subtle p-0">
            {chips.map(chip => {
              const selected = selectedProfiles.includes(chip.id)
              const locked = chip.kind === 'base'
              const tag = tagFor(chip)
              return (
                <li key={chip.id} className="border-b border-line-subtle">
                  <label
                    title={chip.description}
                    className={clsx(
                      'flex items-start gap-3 py-4 pr-1 transition-colors duration-110 ease-carbon',
                      locked || submitting ? 'cursor-default' : 'cursor-pointer hover:bg-layer-hover',
                    )}
                  >
                    <span className="grid h-5 flex-none place-items-center pl-1">
                      {/* Locked for the always-on base, and for ALL rows while the
                          scope→activate chain is in flight — otherwise a mid-submit
                          toggle would drift the gauge/selection away from the
                          profiles already posted to chooseScope (codex P2). */}
                      <Checkbox
                        checked={locked || selected}
                        disabled={locked || submitting}
                        onChange={() => onToggle(chip.id, chip.kind)}
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-body font-semibold text-text-primary">{chip.name}</span>
                        {tag ? <Tag size="sm">{tag}</Tag> : null}
                      </span>
                      <span className="mt-0.5 block text-helper text-text-secondary">{chip.description}</span>
                    </span>
                    <span className="flex-none whitespace-nowrap pt-0.5 text-right text-helper tabular-nums text-text-primary">
                      {chip.countLabel ?? <span className="text-text-helper">not sized</span>}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>

          {/* ── "How these numbers work" explainer (collapsed) ──────── */}
          <button
            type="button"
            onClick={() => setExplainerOpen(o => !o)}
            aria-expanded={explainerOpen}
            className="mt-2 flex min-h-10 w-full items-center gap-2 text-left text-body text-text-secondary transition-colors duration-110 ease-carbon hover:text-text-primary"
          >
            {explainer.toggleLabel}
            <IconChevronRight
              className={clsx('ml-auto text-[color:var(--icon-secondary)] transition-transform duration-150 ease-carbon', explainerOpen && 'rotate-90')}
            />
          </button>
          <div hidden={!explainerOpen} className="pb-2">
            {explainer.items.map(item => (
              <p key={item.lead} className="m-0 py-1.5 text-helper text-text-secondary">
                <b className="font-semibold text-text-primary">{item.lead}</b> {item.body}
              </p>
            ))}
          </div>

          {/* ── Read-back ───────────────────────────────────────────── */}
          <p className="m-0 mt-4 text-body text-text-secondary">
            {selectionSummary ? (
              <>
                Watching <b className="font-semibold text-text-primary">{selectionSummary}</b>.
              </>
            ) : (
              SCOPE_MODAL_COPY.emptySummary
            )}
          </p>
        </ModalFrame>
      </div>
    </div>
  )
}
